// import * as XLSX from 'xlsx'; // Unused import commented out
import * as XLSX from 'xlsx';
// import Papa from 'papaparse'; // Unused import commented out
// import { ShopifyOrderRow } from '../types/order'; // Conflicting import commented out

export interface ParsedFileResult {
  data: any[];
  totalRows: number;
  headers: string[];
}

export const parseOrderFile = async (file: File): Promise<ParsedFileResult> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
  switch (fileExtension) {
    case 'csv':
      return parseCSVFile(file);
    case 'xlsx':
    case 'xls':
      return parseExcelFile(file);
    default:
      throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }
};

async function parseCSVFile(file: File): Promise<ParsedFileResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          resolve({ data: [], totalRows: 0, headers: [] });
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const rows = lines.slice(1).map(line => line.split(',').map(cell => cell.trim().replace(/"/g, '')));
        
        // Convert rows to objects - use different variable name to avoid conflict
        const parsedData = rows.map(row => {
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });

        resolve({
          data: parsedData,
          totalRows: parsedData.length,
          headers
        });
      } catch (error) {
        reject(new Error('Failed to parse CSV file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

async function parseExcelFile(file: File): Promise<ParsedFileResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          resolve({ data: [], totalRows: 0, headers: [] });
          return;
        }
        
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: '',
          raw: false
        }) as any[][];
        
        if (jsonData.length === 0) {
          resolve({ data: [], totalRows: 0, headers: [] });
          return;
        }
        
        // First row contains headers
        const headers = jsonData[0].map(h => String(h).trim());
        const dataRows = jsonData.slice(1).filter(row => 
          row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')
        );
        
        // Convert rows to objects
        const parsedData = dataRows.map(row => {
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] ? String(row[index]).trim() : '';
          });
          return obj;
        });

        resolve({
          data: parsedData,
          totalRows: parsedData.length,
          headers
        });
      } catch (error) {
        reject(new Error('Failed to parse Excel file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

export const validateOrderFileData = (data: any[]): { 
  valid: any[]; 
  errors: Array<{ row: number; error: string }> 
} => {
  const valid: any[] = [];
  const errors: Array<{ row: number; error: string }> = [];

  if (!Array.isArray(data)) {
    errors.push({ row: 0, error: 'Invalid data format' });
    return { valid, errors };
  }
  data.forEach((row, index) => {
    const rowNumber = index + 1;
    
    // Skip gift vouchers
    if (isGiftVoucher(row)) {
      return; // Skip without adding to valid or errors
    }
    
    // Validate required fields for order files
    // Check for both exact field names and common variations
    const orderNumber = row['Order #'] || row['Order'] || row['ORDER #'] || row['ORDER'];
    const item = row['Item'] || row['Title'] || row['ITEM'] || row['TITLE'];
    const sku = row['SKU'] || row['sku'];
    
    if (orderNumber && item && sku) {
      // Normalize the field names
      const normalizedRow = {
        ...row,
        'Order #': orderNumber,
        'Item': item,
        'SKU': sku
      };
      valid.push(normalizedRow);
    } else {
      const missingFields = [];
      if (!orderNumber) missingFields.push('Order #');
      if (!item) missingFields.push('Item');
      if (!sku) missingFields.push('SKU');
      errors.push({ row: rowNumber, error: `Missing required fields: ${missingFields.join(', ')}` });
    }
  });

  return { valid, errors };
};

// Consolidated gift voucher detection function
export const isGiftVoucher = (item: any): boolean => {
  const sku = String(item.SKU || '').toUpperCase();
  const title = String(item.Title || item.Item || '').toUpperCase();
  const handle = String(item.Handle || '').toUpperCase();
  
  // Comprehensive gift voucher patterns
  const giftVoucherPatterns = [
    /GIFT[\s\-_]*VOUCHER/i,
    /GIFT[\s\-_]*CARD/i,
    /VOUCHER/i,
    /GIFTCARD/i,
    /^GV\d+/i,
    /^GIFT/i,
    /EGIFT/i,
    /E[\s\-_]*GIFT/i,
    /DIGITAL[\s\-_]*GIFT/i
  ];
  
  // Check if any pattern matches in SKU, title, or handle
  return giftVoucherPatterns.some(pattern => 
    pattern.test(sku) || pattern.test(title) || pattern.test(handle)
  );
};

// Helper function to extract style info from various SKU formats
export const extractStyleInfoFromSKU = (sku: string): { styleCode: string; size: string; color: string } => {
  if (!sku) {
    return { styleCode: '', size: 'Unknown', color: 'Unknown' };
  }

  const parts = sku.split('-');
  
  if (parts.length >= 3) {
    return {
      styleCode: parts[0],
      size: parts[1],
      color: parts[2]
    };
  }
  
  if (parts.length === 2) {
    return {
      styleCode: parts[0],
      size: parts[1],
      color: 'Unknown'
    };
  }
  
  // Single part (no separators)
  const match = sku.match(/^([A-Z]{2,4})(\d+[A-Z]*)$/);
  if (match) {
    return {
      styleCode: match[1] + match[2],
      size: 'Unknown',
      color: 'Unknown'
    };
  }
  
  // Fallback
  return {
    styleCode: sku,
    size: 'Unknown',
    color: 'Unknown'
  };
};

// Keep the old function for backward compatibility
export const validateShopifyOrderData = (data: any[]): { 
  valid: ShopifyOrderRow[]; 
  errors: Array<{ row: number; error: string }> 
} => {
  const requiredFields = ['Handle', 'Title', 'SKU'];
  
  const valid: ShopifyOrderRow[] = [];
  const errors: Array<{ row: number, error: string }> = [];

  data.forEach((row, index) => {
    const rowNumber = index + 1;
    
    // Skip gift vouchers
    if (isGiftVoucher(row)) {
      return;
    }
    
    // Validate required fields
    const missingFields = requiredFields.filter(field => !row[field]);
    if (missingFields.length > 0) {
      errors.push({
        row: rowNumber,
        error: `Missing required fields: ${missingFields.join(', ')}`
      });
      return;
    }
    
    valid.push(row as ShopifyOrderRow);
  });

  return { valid, errors };
};

// Function to process actual order file data for production use
export const processOrderData = (validatedRows: any[]): {
  orderGroups: Map<string, any[]>;
  summary: {
    totalOrders: number;
    totalItems: number;
    errors: Array<{ row: number; error: string; order?: string }>;
  };
} => {
  const orderGroups = new Map<string, any[]>();
  const summary = {
    totalOrders: 0,
    totalItems: 0,
    errors: [] as Array<{ row: number; error: string; order?: string }>
  };

  validatedRows.forEach((row, index) => {
    try {
      // Group items by order (you'll need to implement order grouping logic)
      const orderNumber = row.Handle || `ORDER_${Math.floor(index / 5) + 1}`;
      
      if (!orderGroups.has(orderNumber)) {
        orderGroups.set(orderNumber, []);
      }
      
      orderGroups.get(orderNumber)!.push(row);
      summary.totalItems++;
    } catch (error) {
      summary.errors.push({
        row: index + 1,
        error: error instanceof Error ? error.message : 'Unknown error',
        order: row.Handle
      });
    }
  });

  summary.totalOrders = orderGroups.size;
  
  return { orderGroups, summary };
};

// Helper function to process Shopify inventory data and filter out gift vouchers
export const processShopifyInventoryData = (data: any[]): {
  validItems: any[];
  skippedGiftVouchers: number;
  errors: Array<{ row: number; error: string }>;
} => {
  const validItems: any[] = [];
  let skippedGiftVouchers = 0;
  const errors: Array<{ row: number; error: string }> = [];

  data.forEach((row, index) => {
    try {
      // Check if item is a gift voucher
      if (isGiftVoucher(row)) {
        skippedGiftVouchers++;
        return;
      }
      
      // Validate the item data
      if (!row.SKU || !row.Title) {
        errors.push({
          row: index + 1,
          error: 'Missing required fields: SKU or Title'
        });
        return;
      }
      
      validItems.push(row);
    } catch (error) {
      errors.push({
        row: index + 1,
        error: error instanceof Error ? error.message : 'Processing error'
      });
    }
  });

  return {
    validItems,
    skippedGiftVouchers,
    errors
  };
};

// Type definition for backward compatibility
interface ShopifyOrderRow {
  Handle: string;
  Title: string;
  SKU: string;
  [key: string]: any;
}
